// Text utilities for the application

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
};

export const capitalizeFirst = (text: string): string => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

export const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/--+/g, "-") // Replace multiple hyphens with single
    .trim();
};

/**
 * Highlight search terms in text with React elements
 * @param text - Text to search in
 * @param searchTerm - Term to highlight
 * @returns Array of React elements (text and highlighted portions)
 */
export const highlightSearchTerm = (
  text: string,
  searchTerm: string,
): (string | JSX.Element)[] => {
  if (!searchTerm || !text) return [text];

  try {
    // Escape special regex characters in search term
    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedSearchTerm})`, "gi");

    // Split text by search term
    const parts = text.split(regex);

    return parts.map((part, index) => {
      // Check if this part matches the search term (case-insensitive)
      if (part.toLowerCase() === searchTerm.toLowerCase()) {
        return (
          <mark
            key={`highlight-${index}`}
            className="bg-yellow-200 px-0.5 rounded"
          >
            {part}
          </mark>
        );
      }

      // Return plain text
      return part;
    });
  } catch (error) {
    // If regex fails, return original text
    console.error("Error highlighting search term:", error);
    return [text];
  }
};

export const wordCount = (text: string): number => {
  return text.trim().split(/\s+/).length;
};

export const readingTime = (text: string, wordsPerMinute = 200): number => {
  const words = wordCount(text);
  return Math.ceil(words / wordsPerMinute);
};

/**
 * Get a user-friendly label for a URL based on the platform
 * @param url - The URL to analyze
 * @returns Friendly display text for the link
 */
const getFriendlyLinkLabel = (url: string): string => {
  const lowercaseUrl = url.toLowerCase();

  // Meeting platform patterns
  if (lowercaseUrl.includes("zoom.us") || lowercaseUrl.includes("zoom.com")) {
    return "Join us on Zoom";
  }
  if (
    lowercaseUrl.includes("teams.microsoft.com") ||
    lowercaseUrl.includes("teams.live.com")
  ) {
    return "Join us on Microsoft Teams";
  }
  if (lowercaseUrl.includes("meet.google.com")) {
    return "Join us on Google Meet";
  }
  if (lowercaseUrl.includes("webex.com")) {
    return "Join us on Webex";
  }
  if (
    lowercaseUrl.includes("gotomeeting.com") ||
    lowercaseUrl.includes("gotomeet.me")
  ) {
    return "Join us on GoToMeeting";
  }
  if (lowercaseUrl.includes("whereby.com")) {
    return "Join us on Whereby";
  }
  if (lowercaseUrl.includes("bluejeans.com")) {
    return "Join us on BlueJeans";
  }

  // For other URLs, show a shortened version if too long
  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname.replace("www.", "");

    // If URL is too long, show domain + "..."
    if (url.length > 50) {
      return `🔗 ${domain}`;
    }

    return url;
  } catch {
    // If URL parsing fails, return the original URL
    return url;
  }
};

/**
 * Convert URLs in text to clickable React elements
 * @param text - Text that may contain URLs
 * @returns Array of React elements (text and links)
 */
export const linkifyText = (text: string): (string | JSX.Element)[] => {
  if (!text) return [""];

  // URL regex pattern that matches http/https URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  // Split text by URLs and create an array of parts
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    // Check if this part is a URL
    if (part.match(urlRegex)) {
      // Sanitize URL to prevent XSS
      const sanitizedUrl = part.trim();
      const displayLabel = getFriendlyLinkLabel(sanitizedUrl);

      return (
        <a
          key={`link-${index}`}
          href={sanitizedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium underline decoration-2 underline-offset-2 hover:decoration-blue-800 transition-colors"
          onClick={(e) => {
            // Validate URL before opening
            try {
              new URL(sanitizedUrl);
            } catch {
              e.preventDefault();
              console.error("Invalid URL:", sanitizedUrl);
            }
          }}
        >
          {displayLabel}
        </a>
      );
    }

    // Return plain text
    return part;
  });
};
